import { useCallback } from "react";

interface ApplicationField {
  name: string;
  label: string;
  type: string;
  required: boolean;
  placeholder?: string;
  options?: string[];
}

const FIELD_TYPES = [
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Long text" },
  { value: "email", label: "Email" },
  { value: "url", label: "URL" },
  { value: "select", label: "Dropdown" },
  { value: "checkbox", label: "Checkbox" },
];

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

export function ApplicationFieldEditor({
  fields,
  onChange,
}: {
  fields: ApplicationField[];
  onChange: (fields: ApplicationField[]) => void;
}) {
  const addField = useCallback(() => {
    onChange([...fields, { name: "", label: "", type: "text", required: true }]);
  }, [fields, onChange]);

  const updateAt = useCallback(
    (idx: number, patch: Partial<ApplicationField>) => {
      const next = fields.map((f, i) => {
        if (i !== idx) return f;
        const updated = { ...f, ...patch };
        if (patch.label !== undefined && !f.name) {
          updated.name = slugify(patch.label);
        }
        return updated;
      });
      onChange(next);
    },
    [fields, onChange],
  );

  const removeAt = useCallback(
    (idx: number) => {
      onChange(fields.filter((_, i) => i !== idx));
    },
    [fields, onChange],
  );

  const moveUp = useCallback(
    (idx: number) => {
      if (idx === 0) return;
      const next = [...fields];
      const prev = next[idx - 1];
      const curr = next[idx];
      if (prev && curr) {
        next[idx - 1] = curr;
        next[idx] = prev;
      }
      onChange(next);
    },
    [fields, onChange],
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700">Application Fields</label>
        <button
          type="button"
          onClick={addField}
          className="text-xs font-medium text-primary-700 hover:text-primary-800"
        >
          + Add field
        </button>
      </div>

      {fields.length === 0 ? (
        <p className="text-sm text-gray-400">
          No custom fields. Candidates will only provide their name, email, and resume.
        </p>
      ) : (
        <div className="space-y-3">
          {fields.map((field, idx) => (
            <div key={idx} className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2">
              <div className="flex items-start gap-2">
                <div className="flex-1 grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={field.label}
                    onChange={(e) => updateAt(idx, { label: e.target.value })}
                    placeholder="Field label"
                    className="input text-xs"
                  />
                  <div className="flex gap-2">
                    <select
                      value={field.type}
                      onChange={(e) => updateAt(idx, { type: e.target.value })}
                      className="input text-xs flex-1"
                    >
                      {FIELD_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                    <label className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => updateAt(idx, { required: e.target.checked })}
                        className="rounded border-gray-300"
                      />
                      Req
                    </label>
                  </div>
                  <input
                    type="text"
                    value={field.name}
                    onChange={(e) => updateAt(idx, { name: e.target.value })}
                    placeholder="field_name (auto-generated)"
                    className="input text-xs text-gray-500"
                  />
                  <input
                    type="text"
                    value={field.placeholder ?? ""}
                    onChange={(e) => updateAt(idx, { placeholder: e.target.value || undefined })}
                    placeholder="Placeholder text (optional)"
                    className="input text-xs"
                  />
                </div>
                <div className="flex flex-col gap-0.5">
                  <button
                    type="button"
                    onClick={() => moveUp(idx)}
                    disabled={idx === 0}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    title="Move up"
                  >
                    <svg
                      className="h-3.5 w-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => removeAt(idx)}
                    className="p-1 text-red-400 hover:text-red-600"
                    title="Remove"
                  >
                    <svg
                      className="h-3.5 w-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              {field.type === "select" && (
                <input
                  type="text"
                  value={field.options?.join(", ") ?? ""}
                  onChange={(e) =>
                    updateAt(idx, {
                      options: e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                  placeholder="Options (comma-separated): Option A, Option B, Option C"
                  className="input text-xs w-full"
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
